import { useState, useMemo } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  TextInput,
  FlatList,
} from "react-native";
import { useRouter } from "expo-router";
import { createDog } from "../../lib/api";
import { Input } from "../../../components/Input";
import { Button } from "../../../components/Button";
import { AKC_BREEDS } from "../../lib/breeds";

export default function AddDogScreen() {
  const router = useRouter();
  const [name, setName] = useState("");
  const [breed, setBreed] = useState("");
  const [breedQuery, setBreedQuery] = useState("");
  const [showBreedList, setShowBreedList] = useState(false);
  const [selectedHandicap, setSelectedHandicap] = useState("");
  const [sex, setSex] = useState("");
  const [registeredName, setRegisteredName] = useState("");
  const [akcNumber, setAkcNumber] = useState("");
  const [historicalPoints, setHistoricalPoints] = useState("");
  const [totalPoints, setTotalPoints] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // On Add Dog there are no logged runs yet, so historical === total. Keep both fields synced.
  function onHistoricalChange(t: string) {
    setHistoricalPoints(t);
    setTotalPoints(t);
  }
  function onTotalChange(t: string) {
    setTotalPoints(t);
    setHistoricalPoints(t);
  }

  const filteredBreeds = useMemo(() => {
    if (!breedQuery.trim()) return AKC_BREEDS;
    const q = breedQuery.toLowerCase();
    return AKC_BREEDS.filter((b) => b.toLowerCase().includes(q));
  }, [breedQuery]);

  // Map handicap selection to a representative height for the API
  const handicapToHeight: Record<string, number> = { "2.0": 10, "1.5": 15, "1.0": 22 };

  async function handleSubmit() {
    if (!name.trim() || !breed.trim() || !selectedHandicap || !sex) {
      setError("Name, breed, sex, and handicap are required");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const newDog = await createDog({
        name: name.trim(),
        breed: breed.trim(),
        heightInches: String(handicapToHeight[selectedHandicap]),
        sex,
        registeredName: registeredName.trim() || undefined,
        akcRegistrationNumber: akcNumber.trim() || undefined,
        historicalPoints: historicalPoints.trim() ? Number(historicalPoints) : undefined,
      } as any);
      // Go to the newly-created dog's profile. (Previously called router.back(),
      // which popped to whatever was underneath — e.g. another dog's profile.)
      if (newDog?.id) {
        router.replace(`/(tabs)/dogs/${newDog.id}` as any);
      } else {
        router.replace("/(tabs)" as any);
      }
    } catch (err: any) {
      setError(err.message || "Failed to create dog");
    } finally {
      setLoading(false);
    }
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={{ flex: 1 }}
      >
        {/* Header */}
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            paddingHorizontal: 16,
            paddingVertical: 14,
            backgroundColor: "#ffffff",
            borderBottomWidth: 1,
            borderBottomColor: "#e5e7eb",
          }}
        >
          <TouchableOpacity onPress={() => router.back()} style={{ marginRight: 12 }}>
            <Text style={{ color: "#1B2A4A", fontSize: 16 }}>← Back</Text>
          </TouchableOpacity>
          <Text style={{ fontSize: 18, fontWeight: "700", color: "#1B2A4A" }}>Add Dog</Text>
        </View>

        <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 60 }}>
          {error ? (
            <View style={{ backgroundColor: "#fef2f2", padding: 12, borderRadius: 8, marginBottom: 16 }}>
              <Text style={{ color: "#dc2626", textAlign: "center" }}>{error}</Text>
            </View>
          ) : null}

          <Input
            label="Dog's Call Name *"
            value={name}
            onChangeText={setName}
            placeholder="e.g. Bella"
            autoCapitalize="words"
          />
          {/* Breed searchable dropdown */}
          <View style={{ marginBottom: 16, zIndex: 10 }}>
            <Text style={{ fontSize: 14, fontWeight: "500", color: "#374151", marginBottom: 6 }}>Breed *</Text>
            <TouchableOpacity
              onPress={() => setShowBreedList(!showBreedList)}
              style={{
                borderWidth: 1,
                borderColor: showBreedList ? "#1B2A4A" : "#d1d5db",
                borderRadius: 10,
                paddingVertical: 12,
                paddingHorizontal: 16,
                backgroundColor: "#fff",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text style={{ fontSize: 16, color: breed ? "#111827" : "#9ca3af" }}>
                {breed || "Select breed..."}
              </Text>
              <Text style={{ color: "#6b7280" }}>{showBreedList ? "▲" : "▼"}</Text>
            </TouchableOpacity>

            {showBreedList && (
              <View style={{
                borderWidth: 1,
                borderColor: "#d1d5db",
                borderRadius: 10,
                backgroundColor: "#fff",
                marginTop: 4,
                maxHeight: 250,
                overflow: "hidden",
              }}>
                <TextInput
                  value={breedQuery}
                  onChangeText={setBreedQuery}
                  placeholder="Search breeds..."
                  autoFocus
                  style={{
                    borderBottomWidth: 1,
                    borderBottomColor: "#e5e7eb",
                    paddingVertical: 10,
                    paddingHorizontal: 14,
                    fontSize: 15,
                    color: "#111827",
                  }}
                  placeholderTextColor="#9ca3af"
                />
                <ScrollView
                  style={{ maxHeight: 200 }}
                  nestedScrollEnabled
                  keyboardShouldPersistTaps="handled"
                >
                  {filteredBreeds.map((item) => (
                    <TouchableOpacity
                      key={item}
                      onPress={() => {
                        setBreed(item);
                        setBreedQuery("");
                        setShowBreedList(false);
                      }}
                      style={{
                        paddingVertical: 11,
                        paddingHorizontal: 14,
                        backgroundColor: item === breed ? "#F0F2F5" : "#fff",
                        borderBottomWidth: 1,
                        borderBottomColor: "#f3f4f6",
                      }}
                    >
                      <Text style={{ fontSize: 15, color: item === breed ? "#1B2A4A" : "#111827", fontWeight: item === breed ? "600" : "400" }}>{item}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            )}
          </View>
          {/* Handicap picker — 3 options per AKC rules */}
          <View style={{ marginBottom: 20 }}>
            <Text style={{ fontSize: 14, fontWeight: "500", color: "#374151", marginBottom: 8 }}>Handicap / Dog Height *</Text>
            <View style={{ flexDirection: "row", gap: 10 }}>
              {[
                { handicap: "2.0", label: "2.0×", desc: "Under 12\"" },
                { handicap: "1.5", label: "1.5×", desc: "12\" – 17.99\"" },
                { handicap: "1.0", label: "1.0×", desc: "18\" and over" },
              ].map((opt) => {
                const selected = selectedHandicap === opt.handicap;
                return (
                  <TouchableOpacity
                    key={opt.handicap}
                    onPress={() => setSelectedHandicap(opt.handicap)}
                    style={{
                      flex: 1,
                      paddingVertical: 14,
                      paddingHorizontal: 8,
                      borderRadius: 10,
                      borderWidth: 2,
                      borderColor: selected ? "#1B2A4A" : "#d1d5db",
                      backgroundColor: selected ? "#F0F2F5" : "#fff",
                      alignItems: "center",
                    }}
                  >
                    <Text style={{ fontSize: 22, fontWeight: "800", color: selected ? "#1B2A4A" : "#374151" }}>
                      {opt.label}
                    </Text>
                    <Text style={{ fontSize: 11, color: selected ? "#1B2A4A" : "#6b7280", marginTop: 4, textAlign: "center" }}>
                      {opt.desc}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {/* Sex picker — required, two options */}
          <View style={{ marginBottom: 20 }}>
            <Text style={{ fontSize: 14, fontWeight: "500", color: "#374151", marginBottom: 8 }}>Sex *</Text>
            <View style={{ flexDirection: "row", gap: 10 }}>
              {[
                { value: "Male", label: "Male" },
                { value: "Female", label: "Female" },
              ].map((opt) => {
                const selected = sex === opt.value;
                return (
                  <TouchableOpacity
                    key={opt.value}
                    onPress={() => setSex(opt.value)}
                    style={{
                      flex: 1,
                      paddingVertical: 14,
                      borderRadius: 10,
                      borderWidth: 2,
                      borderColor: selected ? "#1B2A4A" : "#d1d5db",
                      backgroundColor: selected ? "#F0F2F5" : "#fff",
                      alignItems: "center",
                    }}
                  >
                    <Text style={{ fontSize: 16, fontWeight: "700", color: selected ? "#1B2A4A" : "#374151" }}>
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
          <Input
            label="AKC Registered Name (optional)"
            value={registeredName}
            onChangeText={setRegisteredName}
            placeholder="e.g. CH Bella's Blue Dream"
            autoCapitalize="words"
          />
          <Input
            label="AKC Registration # (optional)"
            value={akcNumber}
            onChangeText={setAkcNumber}
            placeholder="e.g. SR12345678"
            autoCapitalize="characters"
          />
          {/* Section intro — explains when to use historical/total points */}
          <View
            style={{
              backgroundColor: "#EFF6FF",
              borderLeftWidth: 3,
              borderLeftColor: "#1B2A4A",
              padding: 10,
              borderRadius: 6,
              marginBottom: 12,
            }}
          >
            <Text style={{ fontSize: 12, color: "#1B2A4A", lineHeight: 17 }}>
              Use this section if you haven't logged every run in Dash-Hound but know your dog's total AKC Fast CAT points. We'll fill in the gap so titles and rankings match what AKC has on file.
            </Text>
          </View>

          <Input
            label="Historical Points (optional)"
            value={historicalPoints}
            onChangeText={onHistoricalChange}
            placeholder="e.g. 245"
            keyboardType="numeric"
          />
          <Text style={{ fontSize: 12, color: "#6b7280", marginTop: -10, marginBottom: 8 }}>
            Already-earned AKC Fast CAT points before tracking in Dash-Hound. Counts toward title progress and total points only — speed metrics still require logged runs.
          </Text>
          <View style={{ backgroundColor: "#FEE2E2", borderLeftWidth: 4, borderLeftColor: "#DC2626", padding: 10, borderRadius: 6, marginBottom: 14 }}>
            <Text style={{ fontSize: 12, color: "#991B1B", fontWeight: "700" }}>
              Do NOT use this field if you plan on importing or entering each run individually — those runs already add to your total. Using both will double-count your points and incorrectly award titles.
            </Text>
          </View>

          <Input
            label="Total Points (optional)"
            value={totalPoints}
            onChangeText={onTotalChange}
            placeholder="e.g. 245"
            keyboardType="numeric"
          />
          <Text style={{ fontSize: 12, color: "#6b7280", marginTop: -10, marginBottom: 14 }}>
            Type the total your dog has on AKC and we'll back into the historical number for you. (Equal to historical until you start logging runs.)
          </Text>

          <View style={{ marginTop: 8 }}>
            <Button title="Add Dog" onPress={handleSubmit} loading={loading} />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
